<template>
    <div class="refund-review-block">
        <div class="block-header">
            <h3 class="block-title">Review your refund request</h3>
            <p class="block-description">
                Please review the details of your refund request before submitting.
            </p>
        </div>

        <div class="review-summary">
            <div class="info-card">
                <div class="card-header">
                    <svg class="w-6 h-6 mr-3 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 2a4 4 0 00-4 4v1H5a1 1 0 00-.994.89l-1 9A1 1 0 004 18h12a1 1 0 00.994-1.11l-1-9A1 1 0 0015 7h-1V6a4 4 0 00-4-4zM8 6V5a2 2 0 114 0v1H8zm2 6a1 1 0 011 1v2a1 1 0 11-2 0v-2a1 1 0 011-1z" clip-rule="evenodd" />
                    </svg>
                    <h4 class="card-title">License information</h4>
                </div>
                <div class="card-content">
                    <div class="license-details">
                        <div class="detail-row">
                            <span class="detail-label">License type:</span>
                            <span class="detail-value">{{ licenseTierName(license?.slug) }}</span>
                        </div>
                        <div class="detail-row">
                            <span class="detail-label">Billing period:</span>
                            <span class="detail-value">{{ license?.term }} {{ license?.term === 1 ? 'month' : 'months' }}</span>
                        </div>
                        <div class="detail-row">
                            <span class="detail-label">Amount (est.):</span>
                            <span class="detail-value">{{ formatCurrency(license?.price) }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="info-card">
                <div class="card-header">
                    <svg class="w-6 h-6 mr-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                    </svg>
                    <h4 class="card-title">Details</h4>
                </div>
                <div class="card-content">
                    <div class="refund-details">
                        <div class="detail-row">
                            <span class="detail-label">Reason:</span>
                            <span class="detail-value">{{ getReasonText(feedback?.reason) }}</span>
                        </div>
                        <div v-if="feedback?.additionalFeedback" class="detail-row">
                            <span class="detail-label">Additional details:</span>
                            <span class="detail-value">{{ feedback.additionalFeedback }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="info-card">
                <div class="card-header">
                    <svg class="w-6 h-6 mr-3 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                    </svg>
                    <h4 class="card-title">What happens next</h4>
                </div>
                <div class="card-content">
                    <ul class="next-steps">
                        <li>Your license will be canceled immediately</li>
                        <li>You'll receive a refund within 5 to 10 business days</li>
                        <li>You may lose access to some services</li>
                        <li>You can reactivate your account at any time</li>
                    </ul>
                </div>
            </div>

            <div class="info-card guarantee-note">
                <div class="card-header">
                    <svg class="w-6 h-6 mr-3 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                    </svg>
                    <h4 class="card-title">Your money-back guarantee</h4>
                </div>
                <div class="card-content">
                    <p class="guarantee-text">
                        Heads up: your 30-day money-back guarantee is a one-time window that began with your first
                        purchase. Refunding within it is fine, but the window won't reset, and anything you buy after
                        it closes won't be refundable.
                    </p>
                </div>
            </div>
        </div>

        <span class="sr-only" role="status">{{ isSubmitting ? 'Submitting your refund request' : '' }}</span>
        <div class="action-buttons">
            <PrimeButton label="Back" variant="secondary" @click="$emit('goToPage', 3)" />
            <!-- Busy while the refund is out, and still focusable: a disabled button would drop the
                 member's focus to the page. -->
            <PrimeButton
                label="Submit refund request"
                variant="danger"
                :aria-disabled="isSubmitting ? 'true' : undefined"
                :data-busy="isSubmitting ? '' : undefined"
                data-testid="refund-review-submit"
                @click="submitRefund"
            >
                <template #icon>
                    <PbScanMark v-if="isSubmitting" />
                </template>
            </PrimeButton>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { formatCurrency } from '@privacy-bee/format/currency';
import { licenseTierName } from '~/types/licenses/tiers';
import { useBillingStore } from '~/stores/person/billing';
import { useLicenseStore } from '~/stores/person/licenses';
import type { License } from '~/types/person/person';
import { memberFacingMessage } from '~/utils/errors';

interface Props {
    license?: License;
    feedback?: { reason?: string; additionalFeedback?: string };
}

const props = defineProps<Props>();

const emit = defineEmits<{
    close: [];
    goToPage: [page: number];
    terminationPending: [amount: number];
    cancelAutoRenew: [];
    refundError: [errorMessage: string];
}>();

const isSubmitting = ref(false);

const getReasonText = (reason?: string) => {
    const reasons = {
        'too_expensive': 'Too expensive',
        'not_using': 'Not using the service',
        'better_alternative': 'Found a better alternative',
        'technical_issues': 'Technical issues',
        'customer_service': 'Customer service issues',
        'other': 'Other'
    };
    return reasons[reason as keyof typeof reasons] || reason || '';
};

const licenseStore = useLicenseStore();
const billingStore = useBillingStore();

const submitRefund = async () => {
    // One press is one refund, however many times the button is pressed while it is out.
    if (isSubmitting.value) return;
    isSubmitting.value = true;

    try {
        if (!props.license?.id) {
            throw new Error('No license ID provided');
        }

        // The refund endpoint is scoped to the seat's owner: under impersonation that is the
        // member whose license this is, never the staffer driving the session.
        if (!props.license?.owner?.id) {
            throw new Error('No license owner found');
        }

        await licenseStore.refundLicense(
            props.license.owner.id,
            props.license.id,
            props.feedback?.reason || 'No reason provided'
        );

        // The refunded amount is what the refund call returned; the license row carries the
        // plan's price, which is not what was credited back.
        const refundAmount = licenseStore.refundData?.amount ?? 0;
        emit('terminationPending', refundAmount);

        billingStore.fetchInvoices();
    } catch (error: unknown) {
        console.error('Error submitting refund:', error);
        emit('refundError', memberFacingMessage(error, "We couldn't process your refund. Try again in a few minutes."));
    } finally {
        isSubmitting.value = false;
    }
};
</script>

<style lang="scss" scoped>
.refund-review-block {
    @apply p-6;
}

.block-header {
    @apply mb-6 text-center;

    .block-title {
        @apply text-2xl font-semibold text-gray-900 mb-2;
    }

    .block-description {
        @apply text-gray-600 text-base;
    }
}

.review-summary {
    @apply mb-6 space-y-4;
}

.info-card {
    @apply bg-white rounded-lg border border-gray-200 p-6;

    &.guarantee-note {
        @apply bg-amber-50 border-amber-200;

        .guarantee-text {
            @apply text-sm text-gray-700;
        }
    }

    .card-header {
        @apply flex items-center mb-4;

        .card-title {
            @apply text-lg font-semibold text-gray-900;
        }
    }

    .card-content {
        .license-details,
        .refund-details {
            .detail-row {
                @apply flex justify-between items-start py-2 border-b border-gray-100 last:border-b-0;

                .detail-label {
                    @apply font-medium text-gray-700;
                }

                .detail-value {
                    @apply text-gray-900 text-right;
                }
            }
        }

        .next-steps {
            @apply list-disc list-inside space-y-2 text-gray-700;
        }
    }
}

.action-buttons {
    @apply flex justify-center gap-3;
}
</style>